"use client"

import Link from "next/link"
import { ExternalLink, Loader2, Share2, Check } from "lucide-react"
import { toast } from "sonner"
import { useState } from "react"
import { cn } from "@/lib/utils"
import { focusRing, touchTarget, utilityControl } from "@/lib/dashboard-ui"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip"

interface GreetingBarProps {
    firstName: string
    /** Contextual subline, e.g. "58 days until the SAT". */
    subline: string
    isEasyMode: boolean
    onEasyModeChange: (value: boolean) => void
    /** Draws attention to the Access Mode switch right after onboarding. */
    highlightAccessMode?: boolean
    profileUrl: string | null
    onGoToLMS: () => void
    lmsRedirecting?: boolean
}

/**
 * Page header. The old version stacked four equal-weight "3D" cards — Access
 * Mode, LMS, Discord, Share — in a row that scrolled sideways on mobile, so the
 * first thing under the greeting was four things competing to be the primary
 * action. None of them is why a student opens the dashboard.
 *
 * These are utilities, so they are now a compact icon cluster, and Access Mode
 * is a real labelled switch instead of a div styled to look like one.
 */
export function GreetingBar({
    firstName,
    subline,
    isEasyMode,
    onEasyModeChange,
    highlightAccessMode = false,
    profileUrl,
    onGoToLMS,
    lmsRedirecting = false,
}: GreetingBarProps) {
    const [copied, setCopied] = useState(false)

    const handleShare = () => {
        if (!profileUrl) return
        navigator.clipboard.writeText(profileUrl)
        setCopied(true)
        toast.success("Profile link copied", { description: profileUrl })
        setTimeout(() => setCopied(false), 2000)
    }

    return (
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div id="dashboard-tour-header" className="min-w-0 space-y-1">
                <h1 className="text-2xl font-bold tracking-tight text-foreground md:text-3xl text-balance">
                    Welcome back, {firstName}
                </h1>
                <p className="text-sm text-muted-foreground md:text-base">{subline}</p>
            </div>

            <TooltipProvider delayDuration={200}>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    {/* Access Mode — a real switch, so it announces its state and
                        toggles with Space like any other switch. */}
                    <div
                        id="dashboard-tour-access-mode"
                        className={cn(
                            "flex items-center gap-2.5 rounded-xl border border-border bg-card px-3 py-2",
                            "transition-[border-color,box-shadow] duration-200",
                            highlightAccessMode &&
                                "border-primary/60 shadow-lg shadow-primary/20"
                        )}
                    >
                        <Label
                            htmlFor="access-mode"
                            className="cursor-pointer text-sm font-medium text-muted-foreground"
                        >
                            Easy mode
                        </Label>
                        <Switch
                            id="access-mode"
                            checked={isEasyMode}
                            onCheckedChange={onEasyModeChange}
                            aria-describedby="access-mode-hint"
                            className={focusRing}
                        />
                        <span id="access-mode-hint" className="sr-only">
                            Easy mode hides advanced panels and shows a simplified dashboard.
                        </span>
                    </div>

                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                onClick={onGoToLMS}
                                disabled={lmsRedirecting}
                                aria-label="Open the Learning Management System in a new tab"
                                className={cn(utilityControl, touchTarget, "px-3")}
                            >
                                {lmsRedirecting ? (
                                    <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                                ) : (
                                    <ExternalLink className="h-5 w-5" aria-hidden="true" />
                                )}
                                <span className="text-sm font-medium">LMS</span>
                            </button>
                        </TooltipTrigger>
                        <TooltipContent>Open your courses in the LMS</TooltipContent>
                    </Tooltip>

                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Link
                                id="dashboard-tour-discord"
                                href="https://discord.gg/B3VHSQxksT"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Join the SATBuddy Discord community, opens in a new tab"
                                className={cn(utilityControl, touchTarget, "px-3")}
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    className="h-5 w-5"
                                    aria-hidden="true"
                                >
                                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.043.033.055a19.875 19.875 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                                </svg>
                            </Link>
                        </TooltipTrigger>
                        <TooltipContent>Join the Discord community</TooltipContent>
                    </Tooltip>

                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                id="dashboard-tour-share"
                                type="button"
                                onClick={handleShare}
                                disabled={!profileUrl}
                                aria-label="Copy a link to your public profile"
                                className={cn(
                                    utilityControl,
                                    touchTarget,
                                    "px-3",
                                    copied && "border-primary/50 text-primary"
                                )}
                            >
                                {copied ? (
                                    <Check className="h-5 w-5" aria-hidden="true" />
                                ) : (
                                    <Share2 className="h-5 w-5" aria-hidden="true" />
                                )}
                            </button>
                        </TooltipTrigger>
                        <TooltipContent>Copy your profile link</TooltipContent>
                    </Tooltip>

                    {/* Announce the copy result to screen readers — the icon swap alone is silent. */}
                    <span role="status" aria-live="polite" className="sr-only">
                        {copied ? "Profile link copied to clipboard" : ""}
                    </span>
                </div>
            </TooltipProvider>
        </div>
    )
}
